/**
 * @file Utilitaires de deplacement partages par les systemes de chats : bornes d'arene/barriere et resolution des decors solides.
 */
import type { Vec2 } from '@shared/types';
import type { SimContext } from '../Context';
import type { Entity } from '../ecs/World';
import { clampToArena } from '../math/geometry';

const BOUNDS_MARGIN = 0.6;
const BARRIER_INSET = 0.8;

const scratch: Vec2 = { x: 0, y: 0 };

/** Rayon utilisable dans le mode Monster : les chats restent dans la barriere. */
export const barrierBound = (ctx: SimContext): number | null =>
  ctx.modes.currentId === 'monster' ? ctx.config.modes.monster.params.barrierRadius - BARRIER_INSET : null;

/** Borne un point (x, y) a l'arene du mode courant (et au disque de la barriere en Monster) ; resultat dans `out`. */
export const clampPointToBounds = (ctx: SimContext, x: number, y: number, out: Vec2): Vec2 => {
  const a = ctx.modes.rules.arena;
  out.x = x;
  out.y = y;
  clampToArena(out, a.shape, a.halfW, a.halfH, BOUNDS_MARGIN);
  const bound = barrierBound(ctx);
  if (bound !== null) {
    const l = Math.hypot(out.x, out.y);
    if (l > bound && l > 0) {
      out.x *= bound / l;
      out.y *= bound / l;
    }
  }
  return out;
};

/** Borne la position de `e` a l'arene du mode courant (et au disque de la barriere en mode Monster). */
export const clampToBounds = (ctx: SimContext, e: Entity): void => {
  const a = ctx.modes.rules.arena;
  scratch.x = e.transform.x;
  scratch.y = e.transform.y;
  clampToArena(scratch, a.shape, a.halfW, a.halfH, BOUNDS_MARGIN);
  const bound = barrierBound(ctx);
  if (bound !== null) {
    const l = Math.hypot(scratch.x, scratch.y);
    if (l > bound && l > 0) {
      scratch.x *= bound / l;
      scratch.y *= bound / l;
    }
  }
  e.transform.x = scratch.x;
  e.transform.y = scratch.y;
};

/** Repousse `e` hors des colliders `solid` (decors) qui le chevauchent. Retourne vrai si une correction a eu lieu. */
export const resolveSolids = (ctx: SimContext, e: Entity, radius: number): boolean => {
  let pushed = false;
  const ids: number[] = [];
  ctx.rt.targetGrid.queryCircle(e.transform.x, e.transform.y, radius, (id) => ids.push(id));
  for (const id of ids) {
    const o = ctx.rt.world.get(id);
    if (!o || !o.alive || !o.collider || !o.collider.solid) continue;
    const dx = e.transform.x - o.transform.x;
    const dy = e.transform.y - o.transform.y;
    const d = Math.hypot(dx, dy);
    const min = o.collider.radius + radius;
    if (d >= min) continue;
    if (d < 1e-6) {
      e.transform.x += min;
    } else {
      e.transform.x += (dx / d) * (min - d);
      e.transform.y += (dy / d) * (min - d);
    }
    pushed = true;
  }
  return pushed;
};

/** Distance minimale entre deux chats (corps ~0,55 de rayon chacun). */
export const CAT_MIN_DISTANCE = 1.05;
/** Part du deplacement de separation supportee par le chef (il est pilote : ce sont surtout les suiveurs qui s'ecartent). */
const LEADER_YIELD = 0.15;
const SEPARATION_PASSES = 3;

/**
 * Ecarte les chats qui se chevauchent (relaxation par paires, 3 passes) : les suiveurs cedent la place, le chef tres peu, donc un
 * suiveur pris en sandwich entre deux chats est repousse des deux cotes. Quand deux chats sont quasi superposes, la direction de
 * separation est deterministe (selon leur rang) pour casser la symetrie. Les positions sont ensuite reprises dans les bornes
 * de l'arene et hors des decors solides.
 */
export const separateCats = (ctx: SimContext, cats: ReadonlyArray<Entity>): void => {
  if (cats.length < 2) return;
  const moved = new Set<Entity>();
  for (let pass = 0; pass < SEPARATION_PASSES; pass++) {
    let any = false;
    for (let i = 0; i < cats.length; i++) {
      const a = cats[i];
      if (!a) continue;
      for (let j = i + 1; j < cats.length; j++) {
        const b = cats[j];
        if (!b) continue;
        let dx = b.transform.x - a.transform.x;
        let dy = b.transform.y - a.transform.y;
        let d = Math.hypot(dx, dy);
        if (d >= CAT_MIN_DISTANCE) continue;
        if (d < 1e-4) {
          const ang = (i * 2 + j + 1) * 2.399963; // angle d'or : direction deterministe, differente pour chaque paire
          dx = Math.cos(ang);
          dy = Math.sin(ang);
          d = 1;
        }
        const overlap = CAT_MIN_DISTANCE - Math.hypot(b.transform.x - a.transform.x, b.transform.y - a.transform.y);
        const wa = i === 0 ? LEADER_YIELD : 1;
        const wb = j === 0 ? LEADER_YIELD : 1;
        const sa = wa / (wa + wb);
        const nx = dx / d;
        const ny = dy / d;
        a.transform.x -= nx * overlap * sa;
        a.transform.y -= ny * overlap * sa;
        b.transform.x += nx * overlap * (1 - sa);
        b.transform.y += ny * overlap * (1 - sa);
        moved.add(a);
        moved.add(b);
        any = true;
      }
    }
    if (!any) break;
  }
  for (const e of moved) {
    resolveSolids(ctx, e, e.collider?.radius ?? 0.55);
    clampToBounds(ctx, e);
  }
};
